import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { formatUsdFull, formatWibLong, REQ_TYPE_LABEL, REQ_TYPE_VALUES } from "@po/shared";
import { api, ApiError, type AutoCountUserRow, type BudgetDataIssues, type Me, type Settings } from "../lib/api";
import { useSyncNow } from "../lib/sync-now";
import { ProgressBar } from "../components/ProgressBar";
import { StoreSize } from "../components/StoreSize";
import { SyncMonitor } from "../components/SyncMonitor";

type Run = { startedAt: string; finishedAt: string | null; outcome: string; recordsChanged: number; errorType: string | null; errorMessage: string | null } | null;
type SyncStatus = {
  delta: Run;
  nightly: Run;
  backfill: Run;
  budgets: {
    last: Run;
    lastNightly: Run;
    currentError: { type: string | null; message: string | null; at: string } | null;
    guardFailures: { at: string; message: string | null }[];
  };
  strip: { at: string; outcome: string; kind: string }[];
  currentError: { type: string | null; message: string | null; at: string } | null;
  quarantine: { type: string; no: string; firstFailureAt: string; lastFailureAt: string; attempts: number; lastError: string | null }[];
  heartbeatGrace: Record<string, string>;
  alertsNote: string;
};

const when = (s: string | null | undefined) => (s ? formatWibLong(new Date(s)) : "—");

function RunLine({ label, r }: { label: string; r: Run }) {
  return (
    <div className="text-[13px]">
      <span className="font-semibold">{label}:</span>{" "}
      {r ? (
        <>
          {when(r.startedAt)} → {r.finishedAt ? when(r.finishedAt) : "running"} ·{" "}
          <span className={r.outcome === "failed" ? "text-[var(--danger)] font-semibold" : r.outcome === "success" ? "text-[var(--stage-received)]" : ""}>{r.outcome}</span> ·{" "}
          {r.recordsChanged} records changed
        </>
      ) : (
        "never run"
      )}
    </div>
  );
}

function NumberField({ label, value, onChange, suffix }: { label: string; value: number; onChange: (n: number) => void; suffix: string }) {
  return (
    <label className="flex items-center gap-2 text-[13px]">
      <span className="w-48 text-ink2">{label}</span>
      <input type="number" min={0} className="input w-24 tnum" value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="text-muted">{suffix}</span>
    </label>
  );
}

/** A comma-separated list; the text is kept as typed and parsed into items on each change. */
function ListField({ label, value, onChange, hint }: { label: string; value: string[]; onChange: (v: string[]) => void; hint: string }) {
  const [text, setText] = useState(value.join(", "));
  return (
    <label className="flex items-center gap-2 text-[13px]">
      <span className="w-48 text-ink2">{label}</span>
      <input
        className="input flex-1 min-w-0"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(e.target.value.split(",").map((t) => t.trim()).filter(Boolean));
        }}
      />
      <span className="text-muted">{hint}</span>
    </label>
  );
}

/** A run strip of the last 24 h, one bar per run, green or red. */
function Strip({ runs, label }: { runs: SyncStatus["strip"]; label: string }) {
  return (
    <div className="flex gap-[2px] flex-wrap" aria-label={label}>
      {runs.map((r) => (
        <span
          key={r.at + r.kind}
          title={`${r.kind} ${when(r.at)}: ${r.outcome}`}
          className="inline-block w-1.5 h-5 rounded-sm"
          style={{ background: r.outcome === "success" ? "var(--stage-received)" : r.outcome === "failed" ? "var(--stage-rejected)" : "var(--muted)" }}
        />
      ))}
      {!runs.length && <span className="text-muted text-[12.5px]">No runs</span>}
    </div>
  );
}

/** A budget sync failure; a 403 from Sertica reads "missing right" (SPEC §13.2). */
const budgetErrorText = (e: NonNullable<SyncStatus["budgets"]["currentError"]>) =>
  e.type === "missing_right" ? `missing right at ${when(e.at)}: the service account needs ProcurementBudget` : `${e.type} at ${when(e.at)} — ${e.message}`;

function IssueList<T>({ title, hint, rows, render }: { title: string; hint: string; rows: T[]; render: (r: T) => string }) {
  return (
    <div>
      <div className="label">
        {title} ({rows.length})
      </div>
      <p className="text-[11.5px] text-muted mb-1">{hint}</p>
      {rows.length ? (
        <ul className="text-[12.5px] space-y-0.5">
          {rows.map((r, i) => (
            <li key={i}>{render(r)}</li>
          ))}
        </ul>
      ) : (
        <p className="text-muted text-[12.5px]">None</p>
      )}
    </div>
  );
}

/** Budget data issues (SPEC §13.2): what the follow-up cannot take as it is. Needs budget access. */
function BudgetIssues() {
  const q = useQuery({ queryKey: ["admin-budget-issues"], queryFn: () => api<BudgetDataIssues>("/admin/budget-issues") });
  const i = q.data;
  if (q.isError) return <p className="text-[12.5px] text-[var(--danger)]">Could not load the budget data issues.</p>;
  if (!i) return <p className="text-[12.5px] text-muted">Loading…</p>;
  const head = (r: { budgetNo: string; unitName: string | null; period: string | null }) => `${r.budgetNo} · ${r.unitName ?? "—"} · ${r.period ?? "—"}`;
  return (
    <div className="space-y-3">
      <IssueList
        title="Not comparable"
        hint="Non-USD lines, or budgets whose monetary unit is not 1: shown without a budget and never flagged."
        rows={i.notComparable}
        render={(r) => `${head(r)}${r.purchaseTypeNo ? ` · ${r.purchaseTypeNo} · ${r.technicalAccountNo}` : ""}: ${r.reason}`}
      />
      <IssueList
        title="Parent not the sum of its children"
        hint="Sheets show parents as the sum of their lines."
        rows={i.parentMismatch}
        render={(r) => `${head(r)} · ${r.purchaseTypeNo}: parent ${formatUsdFull(r.parentUsd)}, children ${formatUsdFull(r.childrenUsd)}`}
      />
      <IssueList
        title="Duplicate keys within one budget"
        hint="The join sums them."
        rows={i.duplicateKeys}
        render={(r) => `${head(r)} · ${r.purchaseTypeNo} · ${r.technicalAccountNo}: ${r.count} lines`}
      />
      <IssueList
        title="Ambiguous budget"
        hint="More than one live budget for a vessel and period; the latest updated is used."
        rows={i.ambiguous}
        render={(r) => `${r.unitName ?? r.unitNo} · ${r.period ?? "—"}: ${r.budgetNos.join(", ")}; using ${r.chosen}`}
      />
    </div>
  );
}

const LINK_STATUS: Record<string, string> = {
  auto: "Same name",
  suggested: "To review",
  unmatched: "No match",
  confirmed: "Confirmed",
  none: "Not in Sertica",
};

/** One AutoCount user: the Sertica user it is, chosen from the likely ones or typed as a user no. */
function AutoCountUserLine({ r }: { r: AutoCountUserRow }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState(r.serticaUserNo ?? "");
  const decide = useMutation({
    mutationFn: (serticaUserNo: string | null) => api<AutoCountUserRow>(`/admin/autocount-users/${encodeURIComponent(r.userId)}`, { method: "PUT", body: JSON.stringify({ serticaUserNo }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-autocount-users"] }),
  });
  const list = `ac-${r.userId}`;
  return (
    <tr className="border-b border-line align-middle">
      <td className="px-2 py-1 tnum">{r.userId}</td>
      <td className="px-2 py-1">{r.name ?? "—"}</td>
      <td className="px-2 py-1 text-right tnum">{r.pos}</td>
      <td className="px-2 py-1">{LINK_STATUS[r.status] ?? r.status}</td>
      <td className="px-2 py-1">
        <input
          className="rounded-input border border-line px-2 py-0.5 w-[180px]"
          list={list}
          value={pick}
          placeholder="Sertica user no."
          onChange={(e) => setPick(e.target.value)}
          aria-label={`Sertica user for AutoCount user ${r.userId}`}
        />
        <datalist id={list}>
          {r.candidates.map((c) => (
            <option key={c.no} value={c.no}>
              {[c.name, c.group].filter(Boolean).join(" · ")}
            </option>
          ))}
        </datalist>
        {r.serticaUserName && pick === r.serticaUserNo && <span className="text-muted text-[11.5px] ml-2">{r.serticaUserName}</span>}
      </td>
      <td className="px-2 py-1 whitespace-nowrap">
        <button className="text-brand hover:underline disabled:opacity-50" disabled={!pick || decide.isPending} onClick={() => decide.mutate(pick)}>
          Confirm
        </button>
        <span className="text-muted"> · </span>
        <button className="text-brand hover:underline disabled:opacity-50" disabled={decide.isPending} onClick={() => decide.mutate(null)}>
          Not in Sertica
        </button>
        {decide.isError && <span className="text-[var(--danger)] text-[11.5px] ml-2">{decide.error instanceof ApiError && decide.error.status === 404 ? "No Sertica user with that number" : "Not saved"}</span>}
      </td>
    </tr>
  );
}

/**
 * SEA's and PEA's users to Sertica's (SPEC §13.3): the worker links a user of the same name; the
 * likely ones, and those with none, wait here for an admin, whose choice the worker keeps.
 */
function AutoCountUsers() {
  const q = useQuery({ queryKey: ["admin-autocount-users"], queryFn: () => api<AutoCountUserRow[]>("/admin/autocount-users") });
  const [all, setAll] = useState(false);
  if (q.isError) return <p className="text-[12.5px] text-[var(--danger)]">Could not load the SEA/PEA users.</p>;
  if (!q.data) return <p className="text-[12.5px] text-muted">Loading…</p>;
  const open = q.data.filter((r) => r.status === "suggested" || r.status === "unmatched");
  const rows = all ? q.data : open;
  return (
    <div className="space-y-2">
      <p className="text-[11.5px] text-muted">
        The users who created SEA and PEA POs, by AutoCount ID. One with a Sertica user of the same name is linked; the rest wait here. A linked user shows as the Sertica user on its POs, in the
        Created by column and on the Users list. {open.length} to review of {q.data.length}.
      </p>
      <label className="text-[12.5px] inline-flex items-center gap-2">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show all, linked ones too
      </label>
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="text-[12.5px] min-w-full">
            <thead>
              <tr className="text-left border-b border-line">
                <th className="px-2 py-1">AutoCount ID</th>
                <th className="px-2 py-1">Name</th>
                <th className="px-2 py-1 text-right">POs</th>
                <th className="px-2 py-1">Match</th>
                <th className="px-2 py-1">Sertica user</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <AutoCountUserLine key={`${r.userId}:${r.serticaUserNo ?? ""}:${r.status}`} r={r} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted text-[12.5px]">None to review</p>
      )}
    </div>
  );
}

/** "Sync now": queues one full sync (every page's part) and follows the job until it finishes or is stopped. */
/**
 * "Sync now" (`all`): what changed in Sertica on every page, the budgets and SEA/PEA. "Full sync"
 * (`full`): that, then the nightly reconciliation and the full item read, now: about 45 min.
 */
function SyncNowButton({ onDone, full = false }: { onDone: () => void; full?: boolean }) {
  const { state, progress, start, stop } = useSyncNow(onDone, "/admin/sync", { scope: full ? "full" : "all" });
  const busy = state.kind === "running";
  const go = () => {
    if (
      full &&
      !window.confirm(
        "Full sync reads everything from Sertica and SEA/PEA: what changed, every budget's lines, SEA/PEA, then every record checked against Sertica (deleted ones removed, every open PO re-read) and every item (about 25 min). About 45 min in all; Stop keeps what it has read. Start it?",
      )
    )
      return;
    void start();
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <button
          className={`rounded-input font-semibold px-4 py-1.5 inline-flex items-center gap-2 ${full ? "border border-brand-2 text-brand-2" : "bg-brand-2 text-white"}`}
          onClick={busy ? stop : go}
          title={full ? "Everything, including the nightly reconciliation and the full item read: about 45 min" : "What changed in Sertica on every page, the budgets and SEA/PEA: a minute or two"}
        >
          {busy && <span aria-hidden className={`inline-block w-3 h-3 border-2 border-t-transparent rounded-full animate-spin ${full ? "border-brand-2" : "border-white"}`} />}
          {busy ? "Stop" : full ? "Full sync" : "Sync now"}
        </button>
        <span className="text-[12.5px]" role="status">
          {state.kind === "done" && <span className="text-[var(--stage-received)]">{full ? "Full sync finished" : "Sync finished"}</span>}
          {state.kind === "stopped" && <span className="text-muted">Sync stopped</span>}
          {state.kind === "failed" && <span className="text-[var(--danger)]">{state.message}</span>}
        </span>
      </div>
      {busy && <ProgressBar progress={progress} label="Waiting for the sync service…" className="max-w-[420px]" />}
    </div>
  );
}

/** Admin page: settings, sync status and "Sync now" (SPEC §13). */
export function AdminPage() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/me"), staleTime: Infinity });
  const settings = useQuery({ queryKey: ["admin-settings"], queryFn: () => api<Settings>("/admin/settings"), enabled: !!me.data?.isAdmin });
  const sync = useQuery({ queryKey: ["admin-sync"], queryFn: () => api<SyncStatus>("/admin/sync"), enabled: !!me.data?.isAdmin, refetchInterval: 60_000 });
  const [draft, setDraft] = useState<Settings | null>(null);
  useEffect(() => {
    if (settings.data) setDraft(settings.data);
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (s: Settings) => api<Settings>("/admin/settings", { method: "PUT", body: JSON.stringify(s) }),
    onSuccess: (s) => {
      qc.setQueryData(["admin-settings"], s);
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  if (me.data && !me.data.isAdmin) return <div className="p-6">This page is for admins.</div>;
  const s = sync.data;
  return (
    <div className="min-h-screen">
      <header className="bg-navy text-white px-4 py-3 flex items-center gap-4">
        <a href="/" className="font-extrabold">
          PO Status Dashboard
        </a>
        <span className="text-slate-300 text-[13px]">Admin</span>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <section className="card p-4 space-y-3">
          <h2 className="text-[20px] font-bold">Sync monitor</h2>
          <p className="text-[12.5px] text-muted">Every module the sync service runs: its state, last run and last success, and the last 24 h.</p>
          {me.data?.isAdmin && <SyncMonitor />}
        </section>

        <section className="card p-4 space-y-3">
          <h2 className="text-[20px] font-bold">Settings</h2>
          <p className="text-[12.5px] text-muted">Changes apply at once.</p>
          {draft && (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(draft);
              }}
            >
              <div className="label pt-2">Cycle-time target per requisition type</div>
              {REQ_TYPE_VALUES.map((t) => (
                <NumberField key={t} label={REQ_TYPE_LABEL[t]} suffix="days" value={draft.cycleTargets[t]} onChange={(n) => setDraft({ ...draft, cycleTargets: { ...draft.cycleTargets, [t]: n } })} />
              ))}
              <div className="label pt-2">Aging bucket edges</div>
              {[0, 1, 2].map((i) => (
                <NumberField
                  key={i}
                  label={`Edge ${i + 1}`}
                  suffix="days"
                  value={draft.agingEdges[i]!}
                  onChange={(n) => {
                    const e = [...draft.agingEdges] as Settings["agingEdges"];
                    e[i] = n;
                    setDraft({ ...draft, agingEdges: e });
                  }}
                />
              ))}
              <div className="label pt-2">Staleness thresholds</div>
              <NumberField label="Stale after" suffix="min" value={draft.staleness.staleMinutes} onChange={(n) => setDraft({ ...draft, staleness: { ...draft.staleness, staleMinutes: n } })} />
              <NumberField label="Severely stale after" suffix="min" value={draft.staleness.severeMinutes} onChange={(n) => setDraft({ ...draft, staleness: { ...draft.staleness, severeMinutes: n } })} />
              <div className="label pt-2">Sync</div>
              <NumberField label="Watermark overlap" suffix="min" value={draft.overlapMinutes} onChange={(n) => setDraft({ ...draft, overlapMinutes: n })} />
              <div className="label pt-2">Budgets</div>
              <ListField label="Project-funded purchase types" hint="comma-separated" value={draft.projectFunded} onChange={(v) => setDraft({ ...draft, projectFunded: v })} />
              <div className="flex items-center gap-3 pt-2">
                <button className="rounded-input bg-brand-2 text-white font-semibold px-4 py-1.5" disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </button>
                {save.isSuccess && <span className="text-[12.5px] text-[var(--stage-received)]">Saved</span>}
                {save.error && (
                  <span className="text-[12.5px] text-[var(--danger)]">
                    {save.error instanceof ApiError && Array.isArray((save.error.body as { issues?: string[] }).issues)
                      ? (save.error.body as { issues: string[] }).issues.join("; ")
                      : save.error.message}
                  </span>
                )}
              </div>
            </form>
          )}
        </section>

        <section className="card p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-[20px] font-bold">Sync status</h2>
            <div className="flex flex-wrap items-start gap-4">
              <SyncNowButton onDone={() => {
                  void qc.invalidateQueries({ queryKey: ["admin-sync"] });
                  void qc.invalidateQueries({ queryKey: ["admin-sync-monitor"] });
                }} />
              <SyncNowButton full onDone={() => {
                  void qc.invalidateQueries({ queryKey: ["admin-sync"] });
                  void qc.invalidateQueries({ queryKey: ["admin-sync-monitor"] });
                }} />
            </div>
          </div>
          {s && (
            <>
              <RunLine label="Last delta" r={s.delta} />
              <RunLine label="Last nightly" r={s.nightly} />
              <RunLine label="Backfill" r={s.backfill} />
              <div>
                <div className="label mb-1">Last 24 h</div>
                <Strip runs={s.strip.filter((r) => r.kind !== "budgets")} label="Runs in the last 24 hours" />
              </div>
              <div className="text-[13px]">
                <span className="font-semibold">Current error:</span>{" "}
                {s.currentError ? `${s.currentError.type} at ${when(s.currentError.at)} — ${s.currentError.message}` : "none"}
              </div>
              <div>
                <div className="label mb-1">Quarantined records ({s.quarantine.length})</div>
                {s.quarantine.length ? (
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="text-muted text-left">
                        <th>Record</th>
                        <th>First failure</th>
                        <th>Last failure</th>
                        <th className="text-right">Attempts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.quarantine.map((q) => (
                        <tr key={q.type + q.no} className="border-t border-line align-top" title={q.lastError ?? ""}>
                          <td className="py-1">
                            {q.type} {q.no}
                          </td>
                          <td>{when(q.firstFailureAt)}</td>
                          <td>{when(q.lastFailureAt)}</td>
                          <td className="text-right tnum">{q.attempts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted text-[12.5px]">None</p>
                )}
              </div>
              <div className="text-[12.5px] space-y-1">
                <div className="label">Thresholds</div>
                <p>
                  In-app: stale after {settings.data?.staleness.staleMinutes} min, severely stale after {settings.data?.staleness.severeMinutes} min.
                </p>
                <p>
                  Heartbeat grace (monitoring service): delta {s.heartbeatGrace.delta}; nightly {s.heartbeatGrace.nightly}; Sertica reachable {s.heartbeatGrace.serticaReachable}.
                </p>
                <p className="text-muted">{s.alertsNote}</p>
              </div>
            </>
          )}
        </section>

        <section className="card p-4 space-y-3">
          <h2 className="text-[20px] font-bold">Budget sync</h2>
          {s && (
            <>
              <RunLine label="Last budget run" r={s.budgets.last} />
              <RunLine label="Last nightly budget run" r={s.budgets.lastNightly} />
              <div>
                <div className="label mb-1">Last 24 h</div>
                <Strip runs={s.strip.filter((r) => r.kind === "budgets")} label="Budget runs in the last 24 hours" />
              </div>
              <div className="text-[13px]">
                <span className="font-semibold">Current error:</span> {s.budgets.currentError ? budgetErrorText(s.budgets.currentError) : "none"}
              </div>
              <div>
                <div className="label mb-1">Guard failures ({s.budgets.guardFailures.length})</div>
                <p className="text-[11.5px] text-muted mb-1">
                  The run stops and keeps the last good budgets when the headers read differ from Sertica&apos;s count, or when more than 10 % of stored budgets would be deleted.
                </p>
                {s.budgets.guardFailures.length ? (
                  <ul className="text-[12.5px] space-y-0.5">
                    {s.budgets.guardFailures.map((g) => (
                      <li key={g.at}>
                        {when(g.at)} — {g.message}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted text-[12.5px]">None</p>
                )}
              </div>
            </>
          )}
          <h3 className="text-[16px] font-bold pt-2">Budget data issues</h3>
          {me.data?.canSeeBudgets ? <BudgetIssues /> : <p className="text-[12.5px] text-muted">Shown only with the ProcurementBudget right in Sertica.</p>}
        </section>

        <section className="card p-4 space-y-3">
          <h2 className="text-[20px] font-bold">SEA/PEA users</h2>
          {me.data?.isAdmin && <AutoCountUsers />}
        </section>

        <section className="card p-4 space-y-3">
          <h2 className="text-[20px] font-bold">Store</h2>
          <p className="text-[12.5px] text-muted">The database&apos;s size and growth per table, sampled each night, and what the nightly clean-up removes.</p>
          {me.data?.isAdmin && <StoreSize />}
        </section>
      </main>
    </div>
  );
}
